import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import Seal from "@/components/Seal";
import BrandBanner from "@/components/BrandBanner";
import { toast } from "sonner";
import { Loader2, MailWarning, RotateCw } from "lucide-react";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerify(false);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      navigate(user.role === "admin" ? "/admin" : from, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Invalid email or password";
      if (status === 403 && /verify/i.test(detail)) {
        setNeedsVerify(true);
      }
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authApi.resendVerification(email);
      toast.success("Verification email sent — check your inbox");
    } catch {
      toast.error("Could not resend right now");
    } finally { setResending(false); }
  };

  return (
    <div className="wechr-container flex min-h-[70vh] flex-col items-center justify-center py-12">
      <BrandBanner className="mb-6 w-full max-w-2xl" />
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Seal size={40} />
          <CardTitle className="mt-2 font-heading text-xl">Sign in to WECH-RB</CardTitle>
        </CardHeader>
        <CardContent>
          {needsVerify && (
            <div className="mb-4 rounded-xl border border-[rgba(154,52,18,0.3)] bg-[#FFF4ED] p-3" data-testid="login-verify-banner">
              <div className="flex items-start gap-2">
                <MailWarning className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#9A3412]" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#7A2E10]">Please verify your email first</p>
                  <p className="mt-0.5 text-xs text-[#9A3412]">We sent a verification link to {email || "your email"}. Verify it, then sign in.</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={resend} disabled={resending} data-testid="login-resend-button">
                    {resending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RotateCw className="mr-2 h-3.5 w-3.5" />} Resend verification email
                  </Button>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="mt-1" />
            </div>
            <Button type="submit" className="btn-green w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>
          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-[var(--wechr-border)]" />
            <span className="text-xs text-[var(--wechr-muted-text)]">or</span>
            <div className="h-px flex-1 bg-[var(--wechr-border)]" />
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} data-testid="google-login-button">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.9 6.1C12.2 13.2 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-2.8-.4-4H24v7.6h12.6c-.3 2.1-1.6 5.2-4.7 7.3l7.2 5.6c4.3-4 6.8-9.9 6.8-16.5z"/><path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.2-5.6c-2 1.4-4.6 2.3-7.8 2.3-6.4 0-11.8-3.7-13.6-9.8l-7.9 6.1C6.4 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </Button>
          <p className="mt-4 text-center text-sm text-[var(--wechr-muted-text)]">
            No account? <Link to="/signup" className="font-medium text-wgreen-700 hover:underline">Create one</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
